import { zMarshalToBoolean, zMarshalToStrictInt } from '../../ReachApi/Marshallers';
import {
    TimelineSlot,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import { BaseThirdPartyAdWidget } from './BaseThirdPartyAdWidget';
import { z } from 'zod';
import { Duration } from 'luxon';

export type BaseThirdPartyAd = {
    assetUrl: string;

    /** number of seconds */
    duration: number;

    playcount: number;

    /**
     * Indicates whether the ad is the fallback ad.
     */
    isFallback: boolean;

    /**
     * Helps indicate whether this ad is the last slot in
     * the current timeline loop containing a Place Exchange
     * widget content.
     */
    lastAdInLoop: boolean;

    /**
     * string used to uniquely identify ad's HTML element
     * in the DOM.
     */
    adId: string;

    /** file type of this ad's asset */
    filetype: ValidAssetTypes;

    expires?: number;
};

export type ValidAssetTypes = 'video' | 'image';

export type BaseThirdPartyWidgetTimelineSlotParams = {
    ad: BaseThirdPartyAd;
};

export type BaseThirdPartyAdWidgetWidgetTimelineSlot = TimelineSlot<
    BaseThirdPartyWidgetTimelineSlotParams,
    BaseThirdPartyAdWidget
>;

export const mappedBaseThirdPartyFallbackAdFileTypes = {
    video: 'video',
    video_asset: 'video',
    image_asset: 'image',
} as const;

export const ZodBaseThirdPartyAdWidgetConfigurationDetails =
    ZodGenericWidgetConfigurationDetails.extend({
        authorized: z.unknown().transform(zMarshalToBoolean), // unused
        ad_width: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        ad_height: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        duration_in_seconds: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        number_of_ads: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        mute_ads: z.unknown().transform(zMarshalToBoolean),
        fallback_asset_uuid: z.string().min(1),
        fallback_asset_type: z.union([
            z.literal('video'),
            z.literal('video_asset'),
            z.literal('image_asset'),
        ]),
    });

export type BaseThirdPartyAdWidgetConfigurationDetails = z.infer<
    typeof ZodBaseThirdPartyAdWidgetConfigurationDetails
>;

export const ZodBaseThirdPartyAdWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    config: ZodBaseThirdPartyAdWidgetConfigurationDetails,
});

export type BaseThirdPartyAdWidgetConfiguration = z.infer<
    typeof ZodBaseThirdPartyAdWidgetConfiguration
>;

export const vastResponseBodyDataFormatter = z.object({
    id: z.string().min(1),
    popUrl: z
        .string()
        .min(1)
        .transform((popUrlStr) => {
            return popUrlStr.trim();
        }),
    duration: z
        .string()
        .min(1)
        .transform((durationString) => {
            const [hours, minutes, seconds] = durationString.split(':').map(Number);
            const duration = Duration.fromObject({ hours, minutes, seconds });
            const durationAsSeconds: number = duration.as('seconds');
            return durationAsSeconds;
        }),
    assetUrl: z
        .string()
        .min(1)
        .transform((assetUrlStr) => {
            return assetUrlStr.trim();
        }),
    filetype: z
        .string()
        .min(1)
        .transform((filetypeStr) => {
            return filetypeStr.includes('video') ? 'video' : 'image';
        }),
});

export type VastResponseBodyTag = z.infer<typeof vastResponseBodyDataFormatter>;

export type UnformattedVastAdData = {
    adId: string | null;
    popUrl: string | null;
    duration: string | null;
    filetype: string | null;
    mediaFileTextContent: string | null;
};
